import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { CreatePaymentTypeRequest } from "../contracts/CreatePaymentTypeRequest";
import { PaymentTypeResponse } from "../contracts/PaymentTypeResponse";
import { UpdatePaymentTypeRequest } from "../contracts/UpdatePaymentTypeRequest";
import { mapResponseToPaymentType } from "../mappers/PaymentTypeMapper";
import { PaymentType } from "../models/PaymentType";

const BASE = "/paymenttypes";

export const fetchPaymentTypes = createAsyncThunk<
  PaymentType[],
  { includeArchived?: boolean } | void,
  { rejectValue: ApiErrorResponse }
>("paymentTypes/fetchAll", async (args, { rejectWithValue }) => {
  try {
    const includeArchived = (args && args.includeArchived) ?? false;
    const response = await axiosInstance.get<PaymentTypeResponse[]>(BASE, {
      params: { includeArchived },
    });
    return response.data.map(mapResponseToPaymentType);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to load payment types"));
  }
});

export const createPaymentType = createAsyncThunk<
  PaymentType,
  CreatePaymentTypeRequest,
  { rejectValue: ApiErrorResponse }
>("paymentTypes/create", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<PaymentTypeResponse>(BASE, request);
    return mapResponseToPaymentType(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to create payment type"));
  }
});

export const updatePaymentType = createAsyncThunk<
  PaymentType,
  { id: string; request: UpdatePaymentTypeRequest },
  { rejectValue: ApiErrorResponse }
>("paymentTypes/update", async ({ id, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<PaymentTypeResponse>(`${BASE}/${id}`, request);
    return mapResponseToPaymentType(response.data);
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to update payment type"));
  }
});

export const archivePaymentType = createAsyncThunk<
  string,
  string,
  { rejectValue: ApiErrorResponse }
>("paymentTypes/archive", async (id, { rejectWithValue }) => {
  try {
    await axiosInstance.delete(`${BASE}/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(toApiError(error, "Failed to archive payment type"));
  }
});
